import { useCallback, useEffect, useState } from 'react';
import { api, setCsrfToken, RequestFailed } from './api';

/**
 * Session state for one panel. Both panels use this with a different base
 * path, which keeps the two flows identical in shape while keeping them
 * completely separate in fact — different endpoint, different cookie,
 * different user table.
 */

export type Pending = 'enroll_2fa' | 'verify_2fa' | null;

export type SessionState<U, C = undefined> = {
  status: 'loading' | 'anonymous' | 'pending' | 'ready';
  user: U | null;
  context: C | null;
  pending: Pending;
  environment: { appName: string; billingMode?: string } | null;
};

type SessionResponse<U, C> = {
  authenticated: boolean;
  pending?: Pending;
  user?: U;
  client?: C;
  csrfToken?: string;
  environment?: { appName: string; billingMode?: string };
};

export function useSession<U, C = undefined>(base: string) {
  const [state, setState] = useState<SessionState<U, C>>({
    status: 'loading', user: null, context: null, pending: null, environment: null,
  });

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<SessionResponse<U, C>>(`${base}/auth/session`);
      if (res.csrfToken) setCsrfToken(res.csrfToken);
      setState({
        status: res.authenticated ? 'ready' : res.pending ? 'pending' : 'anonymous',
        user: res.user ?? null,
        context: res.client ?? null,
        pending: res.pending ?? null,
        environment: res.environment ?? null,
      });
    } catch {
      setState({ status: 'anonymous', user: null, context: null, pending: null, environment: null });
    }
  }, [base]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await api.post(`${base}/auth/logout`, {});
    } catch {
      // Signing out must always end at signed out, even if the call fails.
    }
    setCsrfToken('');
    setState({ status: 'anonymous', user: null, context: null, pending: null, environment: null });
  }, [base]);

  return { ...state, refresh, signOut };
}

export function messageFor(err: unknown): string {
  if (err instanceof RequestFailed) {
    if (err.error.fields?.length) return err.error.fields.map((f) => f.message).join(' ');
    return err.error.message;
  }
  return 'Something went wrong. Please try again.';
}
